import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw, Search } from "lucide-react";

const LEVELS = ["all", "info", "success", "warn", "error"];

const LEVEL_META = {
  success: { icon: CheckCircle2, cls: "text-emerald-700 border-emerald-700 bg-emerald-50" },
  warn: { icon: AlertTriangle, cls: "text-amber-700 border-amber-600 bg-amber-50" },
  error: { icon: XCircle, cls: "text-rose-700 border-rose-700 bg-rose-50" },
  info: { icon: Info, cls: "text-neutral-700 border-neutral-300 bg-neutral-50" },
};

/** Live-refreshing admin event timeline: upgrades, quota-hits, deletions. */
export default function AdminEventsPanel() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [level, setLevel] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (level !== "all") params.set("level", level);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "200");
      const { data } = await api.get(`/admin/events?${params}`);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [level, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-events-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <p className="ed-eyebrow">{t("admNavEvents")}</p>
        <button
          data-testid="admin-events-refresh"
          onClick={load}
          className="ed-btn ed-btn-outline"
          disabled={loading}
        >
          <RefreshCw size={14} strokeWidth={1.7} className={loading ? "animate-spin" : ""} />
          {t("admEventsRefresh")}
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <div className="flex border border-neutral-300 overflow-x-auto">
          {LEVELS.map((lv) => (
            <button
              key={lv}
              data-testid={`admin-events-level-${lv}`}
              onClick={() => setLevel(lv)}
              className={`px-3 py-1.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] ${
                level === lv ? "bg-neutral-900 text-[#F9F8F6]" : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {t(`admEventsLevel_${lv}`)}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[16rem] relative">
          <Search size={14} strokeWidth={1.7} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="admin-events-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("admEventsSearchPlaceholder")}
            className="ed-input pl-9"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="ed-card p-10 text-center" data-testid="admin-events-empty">
          <p className="font-serif text-lg text-neutral-500">{t("admEventsEmpty")}</p>
        </div>
      ) : (
        <ol className="space-y-3" data-testid="admin-events-list">
          {rows.map((r, i) => {
            const meta = LEVEL_META[r.level] || LEVEL_META.info;
            const Icon = meta.icon;
            const ts = r.created_at ? new Date(r.created_at) : null;
            return (
              <li
                key={r.id || i}
                data-testid={`admin-event-${i}`}
                className={`ed-card p-4 flex gap-4 items-start border-l-4 ${meta.cls.split(" ")[1]}`}
              >
                <div className={`border p-2 shrink-0 ${meta.cls}`}>
                  <Icon size={14} strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-base leading-tight">{r.text}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
                      {ts ? ts.toLocaleString() : "—"}
                    </span>
                    <span className={`font-mono text-[0.6rem] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${meta.cls}`}>
                      {r.level}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
